import React, { useEffect, useRef, useState } from "react";
import { ArrowLeft, Upload, Trash2, Image as ImageIcon, Plus, GripVertical } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { api, uploadImage } from "../api";

const AdminGallery = () => {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [error, setError] = useState("");
  const [draggingId, setDraggingId] = useState(null);
  const [overId, setOverId] = useState(null);
  const [savingOrder, setSavingOrder] = useState(false);
  const inputRef = useRef(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/gallery");
      setItems(r.data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    window.scrollTo(0, 0);
    load();
  }, []);

  const handleUpload = async (files, captions = []) => {
    if (!files || !files.length) return;
    setUploading(true);
    setError("");
    try {
      // Upload each file in parallel, then create gallery items sequentially to preserve order
      const urls = await Promise.all([...files].map((f) => uploadImage(f)));
      for (let i = 0; i < urls.length; i++) {
        await api.post("/gallery", { image: urls[i], caption: captions[i] || "" });
      }
      await load();
    } catch (err) {
      setError(err?.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Remove this image from the homepage gallery?")) return;
    try {
      await api.delete(`/gallery/${id}`);
      await load();
    } catch (err) {
      setError(err?.response?.data?.detail || "Delete failed");
    }
  };

  const updateCaption = async (id, caption) => {
    // Optimistic; backend just stores on next add — for editing, we delete + re-add
    // For simplicity here, we treat caption as edit by direct PATCH.
    // (Backend doesn't yet support PATCH item; quick add of caption-only via re-create is overkill)
    // Keep client-side only for now:
    setItems((prev) => prev.map((it) => (it.id === id ? { ...it, caption } : it)));
  };

  // ── Drag & drop reordering ─────────────────────────────────
  const onTileDragStart = (e, id) => {
    setDraggingId(id);
    e.dataTransfer.effectAllowed = "move";
    try { e.dataTransfer.setData("text/plain", id); } catch (_) {}
  };

  const onTileDragOver = (e, id) => {
    // Only handle internal tile drags, never the file-upload drag
    if (!draggingId) return;
    e.preventDefault();
    e.stopPropagation();
    e.dataTransfer.dropEffect = "move";
    if (id !== overId) setOverId(id);
  };

  const onTileDragLeave = () => setOverId(null);

  const onTileDrop = async (e, targetId) => {
    if (!draggingId) return;
    e.preventDefault();
    e.stopPropagation();
    const sourceId = draggingId;
    setDraggingId(null);
    setOverId(null);
    if (!sourceId || sourceId === targetId) return;

    const next = [...items];
    const fromIdx = next.findIndex((a) => a.id === sourceId);
    const toIdx = next.findIndex((a) => a.id === targetId);
    if (fromIdx === -1 || toIdx === -1) return;
    const [moved] = next.splice(fromIdx, 1);
    next.splice(toIdx, 0, moved);

    setItems(next);
    setSavingOrder(true);
    try {
      await api.patch("/gallery/reorder", { ids: next.map((it) => it.id) });
    } catch (err) {
      setError(err?.response?.data?.detail || "Reorder failed");
      await load();
    } finally {
      setSavingOrder(false);
    }
  };

  const onTileDragEnd = () => {
    setDraggingId(null);
    setOverId(null);
  };

  return (
    <div className="min-h-screen bg-[#121212] pt-24 pb-20" data-testid="admin-gallery-page">
      <div className="max-w-6xl mx-auto px-6">
        <div className="flex items-center gap-4 mb-10">
          <button
            onClick={() => navigate("/admin")}
            className="flex items-center gap-2 text-white/40 hover:text-gold transition-colors text-xs font-body tracking-wider uppercase"
          >
            <ArrowLeft size={13} />
            <span>Dashboard</span>
          </button>
          <div className="h-px flex-1 bg-gold/10" />
        </div>

        <div className="flex items-center gap-3 mb-4">
          <div className="h-px w-8 bg-gold/60" />
          <span className="font-body text-gold text-[0.65rem] font-semibold tracking-[0.4em] uppercase">Homepage Gallery</span>
        </div>
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-10">
          <h1 className="font-display font-light text-5xl text-white leading-tight">
            Stage Moments
          </h1>
          <div className="flex flex-col md:items-end gap-1">
            <p className="font-body text-white/40 text-sm">
              {items.length} {items.length === 1 ? "image" : "images"} live on the homepage
            </p>
            {items.length > 1 && (
              <p
                className="font-body text-white/25 text-[0.6rem] tracking-[0.3em] uppercase flex items-center gap-2"
                data-testid="gallery-reorder-hint"
              >
                {savingOrder ? (
                  <>
                    <span className="w-2.5 h-2.5 border border-gold/30 border-t-gold rounded-full animate-spin" />
                    <span className="text-gold/70">Saving order…</span>
                  </>
                ) : (
                  <>Drag tiles to reorder</>
                )}
              </p>
            )}
          </div>
        </div>

        {/* Upload Zone */}
        <div
          onDragOver={(e) => {
            // Only activate for external file drags, not internal tile reorders
            const types = Array.from(e.dataTransfer?.types || []);
            if (!types.includes("Files")) return;
            e.preventDefault();
            setDragOver(true);
          }}
          onDragLeave={() => setDragOver(false)}
          onDrop={(e) => {
            const types = Array.from(e.dataTransfer?.types || []);
            if (!types.includes("Files")) return;
            e.preventDefault();
            setDragOver(false);
            handleUpload(e.dataTransfer.files);
          }}
          onClick={() => !uploading && inputRef.current?.click()}
          className={`drop-zone rounded-sm p-12 text-center cursor-pointer mb-10 ${dragOver ? "active" : ""} ${uploading ? "opacity-60 cursor-wait" : ""}`}
          data-testid="gallery-dropzone"
        >
          <input
            ref={inputRef}
            type="file"
            accept="image/*"
            multiple
            className="hidden"
            onChange={(e) => handleUpload(e.target.files)}
          />
          {uploading ? (
            <>
              <div className="w-10 h-10 border-2 border-gold/30 border-t-gold rounded-full animate-spin mx-auto mb-4" />
              <p className="font-body text-white/50 text-sm">Uploading and saving to homepage…</p>
            </>
          ) : (
            <>
              <Upload size={36} className="text-gold/40 mx-auto mb-4" />
              <p className="font-body text-white/50 text-sm mb-1">Drag &amp; drop images here, or click to select</p>
              <p className="font-body text-white/25 text-xs">JPG, PNG, WEBP, GIF — Max 8MB per file. Upload as many as you like.</p>
            </>
          )}
        </div>

        {error && (
          <div className="bg-red-500/10 border border-red-500/30 rounded-sm px-4 py-3 mb-6">
            <p className="font-body text-red-400 text-xs">{error}</p>
          </div>
        )}

        {/* Existing Items */}
        {loading ? (
          <div className="py-16 flex items-center justify-center">
            <div className="w-8 h-8 border-2 border-gold/20 border-t-gold rounded-full animate-spin" />
          </div>
        ) : items.length === 0 ? (
          <div className="glass rounded-sm py-20 text-center">
            <ImageIcon size={40} className="text-white/10 mx-auto mb-4" />
            <p className="font-body text-white/30 text-sm">No images yet. Upload some to populate the homepage gallery.</p>
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
            {items.map((item) => (
              <div
                key={item.id}
                draggable
                onDragStart={(e) => onTileDragStart(e, item.id)}
                onDragOver={(e) => onTileDragOver(e, item.id)}
                onDragLeave={onTileDragLeave}
                onDrop={(e) => onTileDrop(e, item.id)}
                onDragEnd={onTileDragEnd}
                className={`relative group rounded-sm overflow-hidden bg-black/40 cursor-grab active:cursor-grabbing transition-all ${
                  draggingId === item.id ? "opacity-40 scale-[0.98]" : ""
                } ${overId === item.id && draggingId && draggingId !== item.id ? "ring-2 ring-gold/70 ring-offset-2 ring-offset-[#121212]" : ""}`}
                style={{ aspectRatio: "1" }}
                data-testid={`gallery-item-${item.id}`}
              >
                <img
                  src={item.image}
                  alt={item.caption || "Gallery"}
                  className="w-full h-full object-cover pointer-events-none"
                  draggable={false}
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                <div
                  className="absolute top-2 left-2 w-9 h-9 rounded-sm bg-black/70 border border-gold/30 text-gold/80 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all pointer-events-none"
                  title="Drag to reorder"
                >
                  <GripVertical size={14} />
                </div>
                <button
                  onClick={() => handleDelete(item.id)}
                  className="absolute top-2 right-2 w-9 h-9 rounded-sm bg-black/80 border border-red-500/40 text-red-400 flex items-center justify-center opacity-0 group-hover:opacity-100 hover:bg-red-500/20 transition-all"
                  title="Delete"
                  data-testid={`gallery-delete-${item.id}`}
                >
                  <Trash2 size={13} />
                </button>
                {item.caption && (
                  <div className="absolute inset-x-0 bottom-0 p-2 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                    <p className="font-body text-white text-xs truncate">{item.caption}</p>
                  </div>
                )}
              </div>
            ))}

            {/* Add tile */}
            <button
              onClick={() => inputRef.current?.click()}
              disabled={uploading}
              className="rounded-sm border border-dashed border-gold/30 bg-gold/5 hover:bg-gold/10 hover:border-gold/60 transition-all flex flex-col items-center justify-center gap-2 text-gold/60 hover:text-gold"
              style={{ aspectRatio: "1" }}
              data-testid="gallery-add-tile"
            >
              <Plus size={24} />
              <span className="font-body text-[0.6rem] tracking-widest uppercase">Add</span>
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminGallery;
